import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import Avatar from "../components/shared/Avatar";
import SafeImage from "../components/shared/SafeImage.jsx";
import RecommendedBadge from "../components/shared/RecommendedBadge";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Search, Users, Eye, Clock, ArrowRight, Send, X, ArrowLeft } from "lucide-react";
import { MusicLoader } from "../components/MusicLoader";
import Navbar from "../components/shared/Navbar";
import FiltersBar from "../components/marketplace/FiltersBar";
import GigCard from "../components/marketplace/GigCard";
import Pagination from "../components/marketplace/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MarketplacePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialFilters = useMemo(
    () => ({
      search: "",
      platform: "all",
      sortBy: "newest",
      minPrice: "",
      maxPrice: "",
      minFollowers: "",
      maxDelivery: "",
    }),
    []
  );

  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    ...initialFilters,
    search: searchParams.get("search") || "",
    platform: searchParams.get("platform") || "all",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [pageRecordAmount] = useState(12);
  const [inviteMode, setInviteMode] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedGig, setSelectedGig] = useState(null);
  const [inviteMessage, setInviteMessage] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);

  const getPlatformColor = (platform) => {
    switch (platform) {
      case "tiktok":
        return "bg-black text-white";
      case "meta":
        return "bg-blue-600 text-white";
      case "spotify":
        return "bg-green-500 text-white";
      default:
        return "bg-zinc-800 text-white";
    }
  };

  const getPlatformImage = (platform) => {
    switch (platform) {
      case "tiktok":
        return "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=400&h=300&fit=crop";
      case "meta":
        return "https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?w=400&h=300&fit=crop";
      case "spotify":
        return "https://images.unsplash.com/photo-1614680376593-902f74cf0d41?w=400&h=300&fit=crop";
      default:
        return "https://images.unsplash.com/photo-1767570928868-6f7b41b94502?w=400&h=300&fit=crop";
    }
  };

  const getGigImage = (gig) => {
    if (gig.image_url) {
      return `${API_URL}${gig.image_url}`;
    }
    return getPlatformImage(gig.platform);
  };

  const fetchGigs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        offset: page * pageRecordAmount,
        limit: pageRecordAmount,
        search: filters.search,
        platform: filters.platform !== "all" ? filters.platform : undefined,
        sortBy: filters.sortBy,
        minPrice: filters.minPrice || undefined,
        maxPrice: filters.maxPrice || undefined,
        minFollowers: filters.minFollowers || undefined,
        maxDelivery: filters.maxDelivery || undefined,
      };
      const response = await axios.get(`${API_URL}/api/gigs`, { params });
      setGigs(Array.isArray(response.data) ? response.data : response.data?.items || []);
    } catch (error) {
      console.error("Error loading gigs", error);
      toast.error("Unable to load gigs");
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchGigs();
  }, [fetchGigs]);

  const [trendingTags, setTrendingTags] = useState([]);
  useEffect(() => {
    axios
      .get(`${API_URL}/api/gigs`, { params: { sortBy: "popular", limit: 60 } })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.items || [];
        const counts = {};
        list.forEach((g) => (g.tags || []).forEach((t) => (counts[t] = (counts[t] || 0) + 1)));
        const top = Object.entries(counts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 8)
          .map(([t]) => t);
        setTrendingTags(top);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (searchParams.get("invite") === "true") {
      setInviteMode(true);
    } else {
      setInviteMode(false);
    }
  }, [searchParams]);

  useEffect(() => {
    setFilters((f) => ({
      ...f,
      search: searchParams.get("search") || "",
      platform: searchParams.get("platform") || "all",
    }));
    setPage(0);
  }, [searchParams]);

  const handleSearch = useCallback(
    (e) => {
      e.preventDefault();
      setPage(0);
      fetchGigs();
    },
    [fetchGigs]
  );

  const applyFilters = () => {
    setPage(0);
    fetchGigs();
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setShowFilters(false);
    setPage(0);
  };

  const exitInviteMode = () => {
    setInviteMode(false);
    setCampaign(null);
  };

  const openInviteModal = (gig, event) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedGig(gig);
    setInviteModalOpen(true);
  };

  const sendInvite = async () => {
    if (!selectedGig) return;
    setSendingInvite(true);
    try {
      toast.success("Invite sent");
      setInviteModalOpen(false);
      setInviteMessage("");
    } catch (error) {
      console.error("Failed to send invite", error);
      toast.error("Unable to send invite");
    } finally {
      setSendingInvite(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      {inviteMode && campaign && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-[#CCFF00] text-black py-3 px-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={exitInviteMode} className="p-1 hover:bg-black/10 rounded">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <span className="font-bold">INVITE MODE:</span>
                <span className="ml-2">Selecting promoters for &ldquo;{campaign.name}&rdquo;</span>
              </div>
            </div>
            <Button
              onClick={exitInviteMode}
              variant="outline"
              size="sm"
              className="border-black text-black hover:bg-black/10"
            >
              <X className="w-4 h-4 mr-1" /> Exit
            </Button>
          </div>
        </div>
      )}

      <div className={`${inviteMode ? "pt-32 sm:pt-40" : "pt-24 sm:pt-28"} pb-6 sm:pb-8 px-4 sm:px-6 md:px-12 lg:px-24 bg-[#0A0A0A] border-b border-white/5`}>
        <div className="max-w-7xl mx-auto">
          <h1
            className="font-['Syne'] text-3xl sm:text-4xl font-extrabold tracking-tight mb-2 text-white"
            data-testid="marketplace-title"
          >
            {inviteMode ? "Select promoters" : "Promotion gigs"}
          </h1>
          <p className="text-zinc-500 text-sm sm:text-base max-w-xl">
            Verified promoters across TikTok, Meta and Spotify.
          </p>

          {!inviteMode && trendingTags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-5" data-testid="trending-tags">
              <span className="text-xs uppercase tracking-widest text-zinc-600 mr-1">Trending</span>
              {trendingTags.map((tag) => {
                const active = filters.search === tag;
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => navigate(active ? "/marketplace" : `/marketplace?search=${encodeURIComponent(tag)}`)}
                    className={`text-xs font-medium px-3 py-1 rounded-full border transition-colors ${
                      active
                        ? "bg-[#CCFF00] text-black border-[#CCFF00]"
                        : "bg-[#141414] text-zinc-300 border-white/10 hover:border-[#CCFF00]/60 hover:text-white"
                    }`}
                    data-testid={`trending-tag-${tag}`}
                  >
                    #{tag}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <FiltersBar
        filters={filters}
        setFilters={setFilters}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        handleSearch={handleSearch}
        applyFilters={applyFilters}
        clearFilters={clearFilters}
      />

      <div className="py-8 sm:py-12 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <MusicLoader size="lg" text="Loading gigs..." />
            </div>
          ) : gigs.length === 0 ? (
            <div
              className="empty-state max-w-2xl mx-auto py-12 sm:py-16 px-4 text-center"
              data-testid="empty-state"
            >
              <div className="empty-state-icon mx-auto">
                <Search className="w-full h-full" />
              </div>
              <h3 className="font-['Syne'] text-xl sm:text-2xl font-bold mb-2">
                {Object.values(filters).some(Boolean)
                  ? "No gigs match your filters"
                  : "No gigs available yet"}
              </h3>
              <p className="text-sm sm:text-base text-zinc-400 mb-6">
                {Object.values(filters).some(Boolean)
                  ? "Try adjusting your search terms or clearing the filters."
                  : user?.role === "promoter"
                  ? "Be the first to list a gig — head to your dashboard to create one."
                  : "Promoters haven't listed any gigs yet. Check back soon!"}
              </p>
              {user?.role === "promoter" ? (
                <Link to="/dashboard/promoter/new">
                  <Button className="btn-primary px-6 py-3" data-testid="empty-create-gig">
                    Create your first gig
                  </Button>
                </Link>
              ) : !user ? (
                <Link to="/register">
                  <Button className="btn-primary px-6 py-3" data-testid="empty-register">
                    Become a Promoter
                  </Button>
                </Link>
              ) : null}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {gigs.map((gig, index) => (
                  <GigCard
                    key={gig.id}
                    gig={gig}
                    index={index}
                    inviteMode={inviteMode}
                    openInviteModal={openInviteModal}
                    navigate={navigate}
                  />
                ))}
              </div>
              <Pagination page={page} setPage={setPage} gigsLength={gigs.length} />
            </>
          )}
        </div>
      </div>

      <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
        <DialogContent className="sm:max-w-md bg-[#141414] border border-[#2A2A2A] text-white">
          <DialogHeader>
            <DialogTitle className="font-['Syne'] text-2xl font-bold text-white">
              Invite Promoter
            </DialogTitle>
          </DialogHeader>
          {selectedGig && (
            <div className="space-y-6 mt-4">
              <div className="bg-[#0A0A0A] border border-[#2A2A2A] p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <Avatar
                    src={selectedGig.promoter_profile_picture}
                    name={selectedGig.promoter_name}
                    size="md"
                  />
                  <div>
                    <div className="font-bold text-white">{selectedGig.promoter_name}</div>
                    <div className="text-sm text-zinc-500">{selectedGig.title}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#2A2A2A]">
                  <span className="text-zinc-500">Gig Price:</span>
                  <span className="text-2xl font-bold text-[#CCFF00]">${selectedGig.price}</span>
                </div>
              </div>
              {campaign && (
                <div className="bg-[#CCFF00]/10 border border-[#CCFF00]/30 p-4 rounded-lg">
                  <div className="text-sm text-[#CCFF00] mb-1">Campaign:</div>
                  <div className="font-bold text-white">{campaign.name}</div>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-zinc-400 mb-2 block">
                  Personal message (optional)
                </label>
                <textarea
                  placeholder="Hi! I'd love to work with you on this campaign..."
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] p-3 text-white"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setInviteModalOpen(false)}
                  disabled={sendingInvite}
                  className="flex-1 border-[#2A2A2A] text-white hover:bg-[#1A1A1A]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendInvite}
                  disabled={sendingInvite}
                  className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B8E600] font-bold"
                >
                  {sendingInvite ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" /> Send Invite
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketplacePage;
